import React, { useEffect, useState, useId, useMemo, useRef } from "react"
import { graphql } from "gatsby"
import { useInnerPageNav, InPageJumpNav } from "terra-one"
import { navigate } from "gatsby-link"
import { v4 as uuidv4 } from "uuid"
import Layout from "../components/Layout/Layout"
import PageSections from "../components/PageSections/GeneralPageSections"
import Seo from "../components/SEO/Seo"
import SkipToContent from "../components/SkipToContent/SkipToContent"
import formatAndRenderInPageJumpNav from "../utils/organism-formatting/InPageJumpNav/FormatAndRenderInPageJumpNav"
import OverlayNoticeConnector from "../components/OverlayNotice/OverlayNoticeConnector"
import { returnActiveOverlayNotice } from "../components/OverlayNotice/OverlayNoticeMethods"
import PromotionBannersWrapper from "../components/PromotionBanner/PromotionBannersWrapper"
import { returnActivePromotionBanner } from "../components/PromotionBanner/PromotionBannerMethods"
import Datalayer from "../components/Datalayer/Datalayer"
import { AuthContext, useStore } from "../context/auth/auth"

import useLocation from "../utils/hooks/useLocation"
import useRegion from "../utils/functions/getRegion"
import useLanguage from "../utils/hooks/useLanguage"
import { formatRegions } from "../utils/config/regionConfig"
import useContentfulCookie from "../utils/hooks/useContentfulCookie"
import EntitlementsForm from "../components/EntitlementsForm/EntitlementsForm"
import TrialWorkFlow from "../components/TrialWorkFlow/TrialWorkFlow"
import formatOnPageNavData from "../utils/organism-formatting/formatOnPageNavData"
import RegionBlockPage from "../components/RegionBlockPage/RegionBlockPage"
import FormatAndRenderActionBlock from "../utils/organism-formatting/ActionBlock/FormatAndRenderActionBlock"
import { redirectPageGeneral } from "../utils/functions/geoRedirect"


let Assistant
if (typeof window !== "undefined") {
  import("mscopilot-integration").then(lib => {
    Assistant = lib.default
    // console.log("Assistant loaded", Assistant)
  })
}

//import { TIDProvider } from "@trimble-oss/trimble-id-react"
const botTokenEndpoint = process.env.GATSBY_BOT_TOKEN_ENDPOINT
// add variables to .env for config and uncomment to test SSO
const tidClientConfig = {
  configurationEndpoint: process.env.GATSBY_BOT_TID_ENDPOINT,
  clientId: process.env.GATSBY_BOT_TID_CLIENT_ID,
  redirectUrl:
    typeof window === "undefined"
      ? `http://localhost:8001/callback`
      : `${window.location.origin}/login`,
  logoutRedirectUrl:
    typeof window === "undefined"
      ? `http://localhost:8001/callback-logout`
      : `${window.location.origin}/logout`,
  scopes: ["openid"],
}


const GeneralPageTemplate = ({ data, pageContext, location }) => {
  const [state, dispatch] = useStore()
  const [regions, setRegions] = useState(
    data?.contentfulMainNavigation?.languageSelector?.regions ||
      data?.contentfulPageGeneral?.regions
  )
  const [showJumpNav, updateshowJumpNav] = useState(false)
  const [productRegionFormatted, setProductRegionFormatted] = useState(null)
  const [videoProgress, setVideoProgress] = useState(null)
  const [regionBlocked, setRegionBlocked] = useState(false)
  const { innerPageNavData, setInnerPageNavData } = useInnerPageNav({
    breadcrumbs: [],
    siblingPages: [],
  })

  const { country } = useLocation(
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName,
    setRegions,
    data?.contentfulMainNavigation?.languageSelector?.regions
  )

  const formattedRegion = useRegion(
    country,
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName
  )

  const { languageSettings, saveLanguageSettings } = useLanguage(
    location,
    formattedRegion,
    regions,
    data?.siteAppData?.newNavigationFormat === true
      ? data?.contentfulSiteNavigation?.languageSelector?.languages
      : data?.contentfulMainNavigation?.languageSelector?.languages
  )
  const userLoggedIn =
    typeof window !== "undefined"
      ? localStorage?.getItem("user_info")
        ? true
        : false
      : false

  const localeFromCookie = languageSettings?.language?.code

  const {
    breadcrumbs,
    siblingPages,
    overlayNotice,
    commonSearches,
    locale,
    fullPath,
    slug,
    isPublicPage,
    products,
    homepage,
    currentSpaceId,
    siteIdentifier,
    __typename,
    seoData,
    trialFormPageData,
    contentfulId,
    promotionBanners
  } = pageContext || {}

  const filteredProducts = products?.filter(prod => {
    return prod?.node?.node_locale === locale
  })

  const formattedProducts = filteredProducts?.map(prod => {
    return prod.node
  })

  // We find the product that matches the sku
  const product = filteredProducts?.find(prod => {
    return (
      prod?.node?.sku ===
      data?.contentfulProductPage?.productTemplate?.productSku
    )
  })

  useContentfulCookie(`${useId()}${contentfulId?.contentful_id}`)

  useEffect(() => {
    redirectPageGeneral("sketchup", "plans-and-pricing", slug)

  }, [])

  useEffect(() => {
    const formatProductRegions = product => {
      const regionUpdate = formatRegions(product?.regions)
      const clonedProduct = JSON.parse(JSON.stringify(product))
      delete clonedProduct["regions"]
      clonedProduct["regions"] = regionUpdate
      return clonedProduct
    }

    if (formattedProducts?.length) {
      setProductRegionFormatted(
        formattedProducts.map(prod => {
          return formatProductRegions(prod)
        })
      )
    }
  }, [formattedRegion, product, country])

  useEffect(() => {
    const checkForRegion = () => {
      // if there are no regions, we will assume the page is available to all regions
      if (
        !data?.contentfulPageGeneral?.regions ||
        !data?.contentfulPageGeneral?.regions?.length
      ) {
        setRegionBlocked(false)
        return
      }

      const userRegion = formattedRegion?.machineName
        ? formattedRegion?.machineName
        : formattedRegion?.region
        ? formattedRegion?.region
        : "international"

      // if the region is global or international, we allow the page to be viewed
      if (
        userRegion.toLowerCase() === "global" ||
        userRegion.toLowerCase() === "international"
      ) {
        setRegionBlocked(false)
      } else {
        // we block the page if the region is in the list
        const findRegionInList = data?.contentfulPageGeneral?.regions?.find(
          region => {
            return (
              region?.machineName?.toLowerCase() ===
                userRegion?.toLowerCase() ||
              region?.name?.toLowerCase() === userRegion?.toLowerCase()
            )
          }
        )
        if (findRegionInList) {
          setRegionBlocked(true)
        } else {
          setRegionBlocked(false)
        }
      }
    }
    if (typeof window !== "undefined") {
      checkForRegion()
    }
  }, [formattedRegion, country, data?.regions])

  const activeOverlayNotice = returnActiveOverlayNotice(overlayNotice)
  const pagePromotionBanner = data?.contentfulPageGeneral?.promotionBanner
  const activePromotionBanners = pagePromotionBanner
    ? returnActivePromotionBanner([pagePromotionBanner])
    : returnActivePromotionBanner(promotionBanners)

  const languageLocale =
    (!data?.contentfulMainNavigation?.disableLanguageRegion &&
      localeFromCookie) ||
    locale

  const editedLocale =
    languageLocale?.toLowerCase() === "en-us"
      ? "en"
      : languageLocale?.toLowerCase()
      ? languageLocale.toLowerCase()
      : "en"

  const globalFooterContent = data?.contentfulGlobalFooter
    ? data?.contentfulGlobalFooter
    : pageContext?.globalFooterFallback

  const footerData = useMemo(() => {
    return {
      ...data?.contentfulFooter,
      ...globalFooterContent,
      contentfulId: data?.contentfulFooter?.contentfulId,
    }
  }, [data])

  useEffect(() => {
    if (breadcrumbs || siblingPages) {
      setInnerPageNavData({ breadcrumbs, siblingPages })
    }
  }, [breadcrumbs, siblingPages, setInnerPageNavData])

  useEffect(() => {
    dispatch({
      type: "SET_LOCALE",
      payload: editedLocale,
    })
  }, [dispatch, editedLocale])

  const canViewPage = () => {
    if (isPublicPage === false && !userLoggedIn) {
      return false
    }
    return true
  }

  useEffect(() => {
    if (!data) {
      return
    }
    if (!canViewPage()) {
      // navigate unauthorized user to "401 unauthorized" page
      localStorage.setItem("blocked_private_page", `/${fullPath}`)
      navigate(`/401`)
      return
    }
    if (
      data?.contentfulPageGeneral?.jumpNavDisplay === true &&
      data?.contentfulPageGeneral?.sections
    ) {
      updateshowJumpNav(true)
    }
  }, [
    data,
    editedLocale,
    state,
    slug,
    userLoggedIn,
    canViewPage,
    regionBlocked,
    fullPath,
  ])

  useEffect(() => {
    const handleContentChange = async event => {
      event.preventDefault()
      await fetch("/__refresh", { method: "POST" })
    }
    window.addEventListener("stackbitObjectsChanged", handleContentChange)

    return () => {
      window.removeEventListener("stackbitObjectsChanged", handleContentChange)
    }
  }, [])


  
    let botBounce = false;
    let botCounter = 0;
    
    const botBouncer = () => {
      
      const botButton = document.getElementsByClassName("tbwch__bot-assistant-button")[0]
      if (botButton) {
        botCounter = botCounter + 1;
        const botIcon = document?.getElementsByClassName(
          "tbwch__bot-assistant-button"
        )[0]
        if (botCounter === 10) {
          botBounce = true;
          botCounter = 0;
        }
        if (botCounter === 6 && botBounce === true) {
          botBounce = false;
          botCounter = 0;
        }
        if (botBounce) {
          botIcon?.classList.add("tbwch__bot-assistant-button--loading")
        } else {
          botIcon?.classList.remove("tbwch__bot-assistant-button--loading")
        }
      }
    }
    useEffect(() => {
      const interval = setInterval(() => {      
        botBouncer()
      }, 1000)
  
      return () => {
        clearInterval(interval)
      }
    }, [])

  const onPageNavData = formatOnPageNavData(
    data?.contentfulPageGeneral,
    editedLocale,
    pageContext?.homepage,
    currentSpaceId,
    products,
    siteIdentifier
  )

  const jumpNavItems = data?.contentfulPageGeneral?.sections
    ? formatAndRenderInPageJumpNav(data?.contentfulPageGeneral?.sections)
    : null
  const jumpNavType = data?.contentfulPageGeneral?.jumpNavType
    ? data?.contentfulPageGeneral?.jumpNavType
    : "Vertical"

  
    const checkForMetaLocator = () => {

      let metaLocale
      const metalocator = data?.contentfulPageGeneral?.sections?.find(type => {
        return type?.__typename === "ContentfulLayoutMetaLocator"
      })

      if (metalocator?.locales) {
        metaLocale = metalocator?.locales?.find(lang => {
          return lang?.key?.toLowerCase() === editedLocale.toLowerCase()
        })
      }
      if (!metaLocale) {
        metaLocale = { key: "en", value: "en-US (3)" }
      }
      return metaLocale
    }

  return (
    <AuthContext.Consumer>
      {([state, dispatch]) => {
        return (
          (isPublicPage || canViewPage()) && (
            <>
              <Datalayer
                data={data}
                pageContext={pageContext}
                location={location}
                videoProgress={videoProgress}
                setVideoProgress={setVideoProgress}
              />
              <SkipToContent />
              {activeOverlayNotice && (
                <OverlayNoticeConnector
                  location={location}
                  overlayNotice={activeOverlayNotice}
                  locale={editedLocale}
                />
              )}
              {activePromotionBanners?.length > 0 && (
                <PromotionBannersWrapper
                  activePromotionBanners={activePromotionBanners}
                  location={location}
                  locale={editedLocale}
                  pageContext={pageContext}
                />
              )}
              <Layout
                region={formattedRegion}
                regionBlocked={regionBlocked}
                innerPageNavData={innerPageNavData}
                rawNavigationData={data?.contentfulMainNavigation}
                rawFlyoutData={data?.contentfulFlyout}
                rawFlyoutCartData={data?.contentfulFlyoutCart}
                rawFooterData={footerData}
                locale={editedLocale}
                location={location}
                commonSearches={commonSearches}
                isPublicPage={isPublicPage}
                rawAppData={data?.siteAppData}
                languageSettings={languageSettings}
                saveLanguageSettings={saveLanguageSettings}
                products={productRegionFormatted}
                homepageSlug={pageContext?.homepage}
                siteIdentifier={siteIdentifier}
                seoStructuredData={data?.contentfulPageGeneral?.json}
                regions={regions}
                showJumpNav={data?.contentfulPageGeneral?.jumpNavDisplay}
                jumpNavItems={jumpNavItems}
                jumpNavType={jumpNavType}
                currentSpaceId={currentSpaceId}
                onPageNavData={onPageNavData}
                metaLocatorLanguage={checkForMetaLocator()}
                siteNavigationData={data?.contentfulSiteNavigation}
              >
                <div
                  data-sb-object-id={data?.contentfulPageGeneral?.contentfulId}
                >
                  <section
                    className="luna-layout__sections page-type--general"
                    id="page-content"
                    data-sb-field-path="sections"
                  >
                    {!regionBlocked &&
                      !trialFormPageData &&
                      data?.contentfulPageGeneral?.sections && (
                        <PageSections
                          pageSections={data.contentfulPageGeneral.sections}
                          location={location}
                          locale={editedLocale}
                          appData={data?.siteAppData}
                          pageContext={pageContext}
                          region={formattedRegion}
                          videoProgress={videoProgress}
                          setVideoProgress={setVideoProgress}
                          siteAppData={data?.siteAppData}
                          currentSpaceId={currentSpaceId}
                          products={products}
                          siteIdentifier={siteIdentifier}
                        />
                      )}
                    {regionBlocked && (
                      <RegionBlockPage
                        pageContent={
                          data?.contentfulRegionBlockPage?.pageContent
                        }
                        location={location}
                        locale={editedLocale}
                        appData={data?.siteAppData}
                        pageContext={pageContext}
                        region={formattedRegion}
                        siteAppData={data?.siteAppData}
                        currentSpaceId={currentSpaceId}
                        products={products}
                        siteIdentifier={siteIdentifier}
                      />
                    )}
                    {showJumpNav === true && jumpNavType === "Vertical" && (
                      <InPageJumpNav
                        pageSections={jumpNavItems}
                        jumpNavTitle={
                          data?.contentfulPageGeneral?.jumpNavTitle
                            ? data?.contentfulPageGeneral?.jumpNavTitle
                            : "This Page"
                        }
                        jumpNavType={"vertical"}
                      />
                    )}
                    {trialFormPageData && (
                      <TrialWorkFlow
                        data={trialFormPageData}
                        appData={data?.siteAppData}
                        locale={editedLocale}
                        setInnerPageNavData={setInnerPageNavData}
                        region={formattedRegion}
                        siteIdentifier={siteIdentifier}
                        regionList={
                          data?.contentfulMainNavigation?.languageSelector
                            ?.regions
                        }
                      />
                    )}
                    {data?.contentfulPageGeneral?.actionBlock && (
                      <FormatAndRenderActionBlock
                        data={
                          data?.contentfulPageGeneral?.actionBlock &&
                          data?.contentfulPageGeneral?.actionBlock[0]
                        }
                        locale={editedLocale}                       
                      />
                    )}

                    {process.env.GATSBY_BOT_ENABLED === "true" &&
                      slug === "help" &&
                      Assistant && (
                        <>
                          <Assistant
                            botTokenEndpoint={botTokenEndpoint}
                            tidClientConfig={tidClientConfig}
                          />
                        </>
                      )}
                  </section>
                </div>
              </Layout>
            </>
          )
        )
      }}
    </AuthContext.Consumer>
  )
}

export default GeneralPageTemplate

export const query = graphql`
  query GeneralPageQuery(
    $id: String
    $locale: String
    $currentSpaceId: String
    $appDataID: String
  ) {
    siteAppData: contentfulSiteApplicationData(
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ...SiteApplicationData
    }
    contentfulFooter(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
      subsites: { eq: $appDataID }
    ) {
      ... on ContentfulFooter {
        ...Footer
      }
    }
    contentfulGlobalFooter(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulGlobalFooter {
        ...GlobalFooter
      }
    }
    contentfulMainNavigation(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulMainNavigation {
        ...MainNavigation
      }
    }
    contentfulPageGeneral(
      spaceId: { eq: $currentSpaceId }
      id: { eq: $id }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulPageGeneral {
        ...GeneralPage
      }
    }
    contentfulRegionBlockPage(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulRegionBlockPage {
        ...RegionBlockPage
      }
    }
    contentfulSiteNavigation(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ...SiteNavigation
    }
    contentfulFlyout(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulFlyout {
        ...Flyout
      }
    }
    contentfulFlyoutCart(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulFlyoutCart {
        ...FlyoutCart
      }
    }
  }
`
export const Head = ({location, params, data, pageContext}) => {
  
const {    
    fullPath,
    slug,
    homepage,
    isHomepage,
    seoData,
    locale
  } = pageContext || {}

  const [regions, setRegions] = useState(
    data?.contentfulMainNavigation?.languageSelector?.regions ||
      data?.contentfulPageBase?.regions
  )

  const { country } = useLocation(
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName,
    setRegions,
    data?.contentfulMainNavigation?.languageSelector?.regions
  )

  const formattedRegion = useRegion(
    country,
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName
  )

  const { languageSettings, saveLanguageSettings } = useLanguage(
    location,
    formattedRegion,
    regions,
    data?.siteAppData?.newNavigationFormat === true
      ? data?.contentfulSiteNavigation?.languageSelector?.languages
      : data?.contentfulMainNavigation?.languageSelector?.languages,
  )

  const localeFromCookie = languageSettings?.language?.code

  const languageLocale =
    (!data?.contentfulMainNavigation?.disableLanguageRegion &&
      localeFromCookie) ||
    locale

  const editedLocale =
    languageLocale?.toLowerCase() === "en-us"
      ? "en"
      : languageLocale?.toLowerCase()
      ? languageLocale.toLowerCase()
      : "en"

      const pageSeoImage = data?.contentfulPageGeneral?.pageSeo?.[0]?.opengraphImage
    ?.image
    ? `https:` +
      data?.contentfulPageGeneral?.pageSeo?.[0]?.opengraphImage?.image?.file?.url
    : null
  
  return (
    <Seo
      title={
        data?.contentfulPageGeneral?.pageSeo?.[0]?.title
          ? data?.contentfulPageGeneral?.pageSeo?.[0]?.title
          : data?.contentfulPageGeneral?.publicTitle
          ? data?.contentfulPageGeneral?.publicTitle
          : data?.jobPostingId
      }
      description={
        data?.pageSeo?.[0]?.description
          ? data?.pageSeo?.[0]?.description
          : data?.contentfulPageGeneral?.publicTitle
          ? data?.contentfulPageGeneral?.publicTitle
          : data?.jobPostingId
      }
      seoCompose={
        data?.contentfulPageGeneral?.pageSeo?.[0]
          ? data?.contentfulPageGeneral?.pageSeo?.[0]
          : seoData
          ? seoData
          : data?.contentfulPageGeneral?.pageSeo?.[0]
          ? data?.contentfulPageGeneral?.pageSeo?.[0]
          : null
      }
      robotsSettings={{ 
        "no_follow": data?.contentfulPageGeneral?.pageSeo?.[0]?.no_follow, 
        "no_index": data?.contentfulPageGeneral?.pageSeo?.[0]?.no_index
      }}
      fullPath={fullPath}
      slug={slug}
      locale={editedLocale}
      homepage={homepage}
      isHomepage={isHomepage}
      appData={data?.siteAppData}
      navigationData={data?.siteAppData?.newNavigationFormat === true
        ? data?.contentfulSiteNavigation
        : data?.contentfulMainNavigation}
      image={pageSeoImage}
    />
  )
}